/**
 * tdesign v0.44.2
 * (c) 2022 tdesign
 * @license MIT
 */

import { _ as _defineProperty } from '../_chunks/dep-74ef9ead.js';
import { _ as _slicedToArray } from '../_chunks/dep-784849f3.js';
import { _ as _objectWithoutProperties } from '../_chunks/dep-5e9bbb71.js';
import React, { forwardRef, useMemo } from 'react';
import classNames from 'classnames';
import useConfig from '../hooks/useConfig.js';
import useDomRefCallback from '../hooks/useDomRefCallback.js';
import useRipple from '../_util/useRipple.js';
import { Loading } from '../loading/index.js';
import { buttonDefaultProps } from './defaultProps.js';
import parseTNode from '../_util/parseTNode.js';
import '../_chunks/dep-d6cc3db8.js';
import '../config-provider/ConfigContext.js';
import '../_chunks/dep-5eadc4f3.js';
import '../_chunks/dep-7a9656a6.js';
import '../_chunks/dep-fd215ab0.js';
import '../_chunks/dep-8127363b.js';
import '../_chunks/dep-a0b71ae4.js';
import '../_chunks/dep-670cc86e.js';
import '../_chunks/dep-da112583.js';
import '../_chunks/dep-dfae10c3.js';
import '../_chunks/dep-f4495ac9.js';
import '../_chunks/dep-6c09308a.js';
import '../_chunks/dep-4ca52001.js';
import '../_chunks/dep-408df89a.js';
import '../_chunks/dep-e755a9cb.js';
import '../_chunks/dep-1e4c771d.js';
import '../_chunks/dep-15d0e734.js';
import '../_chunks/dep-b0c02324.js';
import '../_chunks/dep-a09b6dac.js';
import '../_chunks/dep-1792ab93.js';
import '../_chunks/dep-9c2f4c72.js';
import '../_chunks/dep-da7344ec.js';
import '../_chunks/dep-01fe0fd1.js';
import '../_chunks/dep-20a8e46d.js';
import '../_chunks/dep-9d067299.js';
import '../locale/zh_CN.js';
import '../_common/js/global-config/locale/zh_CN.js';
import '../_common/js/global-config/default-config.js';
import '../_util/useAnimation.js';
import '../_util/setStyle.js';
import '../_util/dom.js';
import 'raf';
import '../_chunks/dep-10140b37.js';
import '../_util/easing.js';
import '../loading/Loading.js';
import '../common/Portal.js';
import 'react-dom';
import '../loading/gradient.js';
import '../_common/js/loading/circle-adapter.js';
import '../_common/js/utils/set-style.js';
import '../_common/js/utils/helper.js';
import '../_chunks/dep-0508545e.js';
import '../loading/defaultProps.js';
import '../loading/plugin.js';
import '../_util/react-render.js';
import '../_chunks/dep-d07435ee.js';
import '../loading/style/index.js';
import '../loading/type.js';

var _excluded = ["type", "theme", "variant", "icon", "disabled", "loading", "size", "block", "ghost", "shape", "children", "content", "className", "suffix", "href", "tag", "onClick"];
function ownKeys(object, enumerableOnly) { var keys = Object.keys(object); if (Object.getOwnPropertySymbols) { var symbols = Object.getOwnPropertySymbols(object); enumerableOnly && (symbols = symbols.filter(function (sym) { return Object.getOwnPropertyDescriptor(object, sym).enumerable; })), keys.push.apply(keys, symbols); } return keys; }
function _objectSpread(target) { for (var i = 1; i < arguments.length; i++) { var source = null != arguments[i] ? arguments[i] : {}; i % 2 ? ownKeys(Object(source), !0).forEach(function (key) { _defineProperty(target, key, source[key]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(target, Object.getOwnPropertyDescriptors(source)) : ownKeys(Object(source)).forEach(function (key) { Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key)); }); } return target; }
var Button = /*#__PURE__*/forwardRef(function (props, ref) {
  var _classNames;
  var type = props.type,
    theme = props.theme,
    variant = props.variant,
    icon = props.icon,
    disabled = props.disabled,
    loading = props.loading,
    size = props.size,
    block = props.block,
    ghost = props.ghost,
    shape = props.shape,
    children = props.children,
    content = props.content,
    className = props.className,
    suffix = props.suffix,
    href = props.href,
    tag = props.tag,
    onClick = props.onClick,
    buttonProps = _objectWithoutProperties(props, _excluded);
  var _useConfig = useConfig(),
    classPrefix = _useConfig.classPrefix;
  var _useDomRefCallback = useDomRefCallback(),
    _useDomRefCallback2 = _slicedToArray(_useDomRefCallback, 2),
    btnDom = _useDomRefCallback2[0],
    setRefCurrent = _useDomRefCallback2[1];
  useRipple((ref === null || ref === void 0 ? void 0 : ref.current) || btnDom);
  var renderChildren = content !== null && content !== void 0 ? content : children;
  var iconNode = icon;
  if (loading) iconNode = /* @__PURE__ */React.createElement(Loading, {
    loading: loading,
    inheritColor: true
  });
  var renderTheme = useMemo(function () {
    if (!theme) {
      if (variant === "base") return "primary";
      return "default";
    }
    return theme;
  }, [theme, variant]);
  var renderTag = useMemo(function () {
    if (!tag && href) return "a";
    if (!tag && disabled) return "div";
    return tag || "button";
  }, [tag, href, disabled]);
  return /*#__PURE__*/React.createElement(renderTag, _objectSpread(_objectSpread({}, buttonProps), {}, {
    href: href,
    type: type,
    ref: ref || setRefCurrent,
    disabled: disabled || loading,
    className: classNames(className, ["".concat(classPrefix, "-button"), "".concat(classPrefix, "-button--theme-").concat(renderTheme), "".concat(classPrefix, "-button--variant-").concat(variant)], (_classNames = {}, _defineProperty(_classNames, "".concat(classPrefix, "-button--shape-").concat(shape), shape !== "rectangle"), _defineProperty(_classNames, "".concat(classPrefix, "-button--ghost"), ghost), _defineProperty(_classNames, "".concat(classPrefix, "-is-loading"), loading), _defineProperty(_classNames, "".concat(classPrefix, "-is-disabled"), disabled), _defineProperty(_classNames, "".concat(classPrefix, "-size-s"), size === "small"), _defineProperty(_classNames, "".concat(classPrefix, "-size-l"), size === "large"), _defineProperty(_classNames, "".concat(classPrefix, "-size-full-width"), block), _classNames)),
    onClick: !disabled && !loading ? onClick : void 0
  }), /* @__PURE__ */React.createElement(React.Fragment, null, iconNode, renderChildren && /* @__PURE__ */React.createElement("span", {
    className: "".concat(classPrefix, "-button__text")
  }, renderChildren), suffix && /* @__PURE__ */React.createElement("span", {
    className: "".concat(classPrefix, "-button__suffix")
  }, parseTNode(suffix))));
});
Button.displayName = "Button";
Button.defaultProps = buttonDefaultProps;

export { Button as default };
//# sourceMappingURL=Button.js.map
